import { omit } from "lodash";
import { DateTime } from "luxon";
import React, { FC } from "react";

import { Card, CardContent, Skeleton } from "@mui/material";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  AllSeriesType,
  ChartContainer,
  ChartsLegend,
  ChartsTooltip,
  PiePlot,
} from "@mui/x-charts";

import useTimePeriod from "../../../hooks/use-time-period";
import { useSelector } from "../../../store";
import {
  GetTimeSeriesSumApiArg,
  useGetTimeSeriesSumQuery,
} from "../../../store/pomeriumApi";

const AuthorizedRequests: FC = () => {
  const theme = useTheme();
  const { period } = useTimePeriod();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useSelector((state) => state.metrics);
  const now = DateTime.now().startOf("minute");
  const start = now.minus(period);
  let params: GetTimeSeriesSumApiArg = {
    organizationId,
    clusterId,
    startTime: start.toISO(),
    endTime: now.toISO(),
    routeId: routeId,
    metricId: "authz_ok",
  };
  if (!routeId) {
    params = omit(params, ["routeId"]);
  }

  const { data: ok } = useGetTimeSeriesSumQuery(params, {
    skip: !organizationId || !clusterId,
  });
  const countOK = ok?.reduce((count, row) => count + row.value, 0) || 0;
  const { data: err } = useGetTimeSeriesSumQuery(
    { ...params, metricId: "authz_err" },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const countERR = err?.reduce((count, row) => count + row.value, 0) || 0;
  const { data: denied } = useGetTimeSeriesSumQuery(
    { ...params, metricId: "authz_denied" },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const countDENIED = denied?.reduce((count, row) => count + row.value, 0) || 0;
  const loading = !ok || !err || !denied;

  const data = [];
  if (countOK) {
    data.push({
      id: 0,
      value: Math.abs(countOK),
      label: "Authorized",
      color: theme.palette.success.main,
    });
  }
  if (countERR) {
    data.push({
      id: 1,
      value: Math.abs(countERR),
      label: "Error",
      color: theme.palette.error.main,
    });
  }

  if (countDENIED) {
    data.push({
      id: 2,
      value: Math.abs(countDENIED),
      label: "Denied",
      color: theme.palette.warning.main,
    });
  }

  return (
    <Card
      sx={{
        width: "100%",
        m: 2,
      }}
    >
      <CardContent sx={{ height: "40vh", m: 2 }}>
        <Typography variant={"h4"} color="text.primary" gutterBottom>
          Authorized Requests
        </Typography>
        {loading && (
          <Skeleton width="100%" height="100%" sx={{ transform: "unset" }} />
        )}
        {!data.length && !loading && (
          <Alert severity="info">
            No data to display. Data is updated hourly.
          </Alert>
        )}
        {!!(countOK || countERR || countDENIED) && (
          <ChartContainer
            series={[
              {
                id: "authorized-requests",
                type: "pie",
                data: data,
              } as AllSeriesType,
            ]}
          >
            <ChartsLegend direction="horizontal" />
            <ChartsTooltip trigger="item" />
            <PiePlot />
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default AuthorizedRequests;
